import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { AuditService, AuditEvent } from '@core/services/audit.service';
import { environment } from '@env/environment';

const METHOD_ACTION_MAP: Record<string, AuditEvent['action']> = {
  POST: 'CREATE',
  PUT: 'UPDATE',
  PATCH: 'UPDATE',
  DELETE: 'DELETE',
};

/**
 * Interceptor funcional que captura mutations (POST, PUT, PATCH, DELETE)
 * e registra automaticamente no AuditService.
 *
 * GETs nao sao capturados — views de dados sensiveis usam AuditAccessDirective.
 */
export const auditInterceptor: HttpInterceptorFn = (req, next) => {
  const action = METHOD_ACTION_MAP[req.method];

  if (!action) {
    return next(req);
  }

  const isApiRequest = req.url.startsWith(environment.apiUrl);
  if (!isApiRequest) {
    return next(req);
  }

  const auditService = inject(AuditService);
  const parsed = parseApiPath(req.url);

  if (!parsed) {
    return next(req);
  }

  return next(req).pipe(
    tap({
      next: () => {
        auditService.track({
          action,
          domain: parsed.domain,
          resource: parsed.resource,
          resourceId: parsed.resourceId,
        });
      },
    }),
  );
};

interface ParsedApiPath {
  domain: string;
  resource: string;
  resourceId?: string;
}

function parseApiPath(url: string): ParsedApiPath | null {
  const apiPrefix = `${environment.apiUrl}/v1/`;
  const idx = url.indexOf(apiPrefix);
  if (idx === -1) return null;

  const pathAfterVersion = url.substring(idx + apiPrefix.length).split('?')[0];
  const segments = pathAfterVersion.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const domain = segments[0];
  const resource = segments.length > 1 ? segments[1] : domain;
  const resourceId = segments.length > 2 ? segments[2] : undefined;

  return { domain, resource, resourceId };
}
